import type { TFunction } from 'i18next';

import type { Goal } from '@feathr/blackbox';

export const DEFAULT_GOAL_NAME = 'Untitled goal';

/**
 * Validates a goal name against blank, placeholder, and duplicate conditions.
 *
 * @param name - The current goal name.
 * @param existingNames - Names of all non-ephemeral goals in the account.
 * @param t - i18next translation function.
 * @returns Error strings; empty when the name is valid.
 */
export function getGoalNameErrors(name: string, existingNames: string[], t: TFunction): string[] {
  const errors: string[] = [];

  if (!name) {
    errors.push(t("Name can't be blank."));
  } else if (name === DEFAULT_GOAL_NAME) {
    errors.push(t('Change the goal name to continue.'));
  }

  if (name && existingNames.includes(name)) {
    errors.push(
      t(
        "A goal named '{{-name}}' already exists in this account. Choose a different name to continue.",
        { name },
      ),
    );
  }

  return errors;
}

/**
 * Name errors take display priority over model-level validation errors.
 *
 * @param nameErrors - Errors from `getGoalNameErrors`.
 * @param goal - The ephemeral goal model, or null.
 * @returns Error strings; empty when the goal is valid.
 */
export function getValidationErrors(nameErrors: string[], goal: Goal | null): string[] {
  if (goal === null || nameErrors.length > 0) {
    return nameErrors;
  }
  return goal.validate(['conv_type', 'segment'], false).errors;
}
